// Prompt Editor Component
// View and edit AI prompts with live template preview

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api/client'
import {
  MessageSquare,
  ChevronLeft,
  Code,
  Eye,
  AlertCircle,
  Loader2,
  Sparkles,
  FileText,
  Thermometer,
  Hash,
  Cpu,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface PromptVariable {
  name: string
  type: 'string' | 'number' | 'boolean' | 'array' | 'object'
  description: string
  required?: boolean
}

interface AIPrompt {
  id: string
  key: string
  name: string
  description: string | null
  category: string
  icon: string
  color: string | null
  systemPrompt: string
  userTemplate: string
  variables: PromptVariable[]
  outputFormat: Record<string, unknown> | null
  defaultModel: string
  defaultTemperature: number
  defaultMaxTokens: number
  isActive: boolean
  isSystem: boolean
}

interface PromptEditorProps {
  promptKey: string
  onClose: () => void
}

// ============================================================================
// DATA FETCHING
// ============================================================================

async function fetchPrompt(key: string): Promise<AIPrompt> {
  const response = await apiClient.get<{ prompt: AIPrompt }>(`/api/prompts/${key}`)
  return response.data.prompt
}

// ============================================================================
// TEMPLATE PREVIEW
// ============================================================================

function processTemplate(
  template: string,
  variables: Record<string, unknown>
): string {
  let result = template

  // Simple variable substitution
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
    result = result.replace(regex, String(value ?? ''))
  }

  // Process conditionals
  const ifRegex = /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g
  result = result.replace(ifRegex, (_, varName, content) => {
    const value = variables[varName]
    const isTruthy =
      value !== undefined &&
      value !== null &&
      value !== false &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    return isTruthy ? content : ''
  })

  // Process loops
  const eachRegex = /\{\{#each\s+(\w+)\}\}([\s\S]*?)\{\{\/each\}\}/g
  result = result.replace(eachRegex, (_, varName, content) => {
    const items = variables[varName]
    if (!Array.isArray(items) || items.length === 0) return ''

    return items
      .map((item) => {
        let itemContent = content
        if (typeof item === 'object' && item !== null) {
          for (const [key, value] of Object.entries(item)) {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
            itemContent = itemContent.replace(regex, String(value ?? ''))
          }
        } else {
          itemContent = itemContent.replace(/\{\{this\}\}/g, String(item))
        }
        return itemContent
      })
      .join('')
  })

  // Clean up remaining placeholders
  result = result.replace(/\{\{\w+\}\}/g, '')

  return result
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PromptEditor({ promptKey, onClose }: PromptEditorProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'edit'>('preview')
  const [testVariables, setTestVariables] = useState<Record<string, unknown>>({})

  const {
    data: prompt,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ai-prompt', promptKey],
    queryFn: () => fetchPrompt(promptKey),
  })

  // Initialize test variables from prompt schema
  useMemo(() => {
    if (prompt?.variables) {
      const defaults: Record<string, unknown> = {}
      prompt.variables.forEach((v) => {
        if (v.type === 'string') defaults[v.name] = v.required ? `[${v.name}]` : ''
        else if (v.type === 'number') defaults[v.name] = 0
        else if (v.type === 'boolean') defaults[v.name] = false
        else if (v.type === 'array') defaults[v.name] = []
        else defaults[v.name] = {}
      })
      setTestVariables(defaults)
    }
  }, [prompt?.variables])

  const previewContent = useMemo(() => {
    if (!prompt) return ''
    return processTemplate(prompt.userTemplate, testVariables)
  }, [prompt, testVariables])

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </CardContent>
      </Card>
    )
  }

  if (error || !prompt) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center gap-2 text-solution py-8">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load prompt</span>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onClose}>
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: prompt.color ? `${prompt.color}20` : 'var(--color-warm-100)' }}
              >
                <MessageSquare
                  className="w-5 h-5"
                  style={{ color: prompt.color || 'var(--color-text-muted)' }}
                />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{prompt.name}</CardTitle>
                <CardDescription className="text-text-muted">
                  {prompt.description || 'No description'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{prompt.key}</Badge>
              <Badge
                className={
                  prompt.isActive
                    ? 'bg-market/10 text-market border border-market/20'
                    : 'bg-warm-100 text-text-muted'
                }
              >
                {prompt.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Prompt Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Cpu className="w-4 h-4" />
            Model Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg">
              <Sparkles className="w-4 h-4 text-business" />
              <div>
                <p className="text-xs text-text-muted">Model</p>
                <p className="text-sm font-medium text-text">{prompt.defaultModel}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg">
              <Thermometer className="w-4 h-4 text-solution" />
              <div>
                <p className="text-xs text-text-muted">Temperature</p>
                <p className="text-sm font-medium text-text">{prompt.defaultTemperature}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg">
              <Hash className="w-4 h-4 text-market" />
              <div>
                <p className="text-xs text-text-muted">Max Tokens</p>
                <p className="text-sm font-medium text-text">{prompt.defaultMaxTokens}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Variables */}
      {prompt.variables && prompt.variables.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Code className="w-4 h-4" />
              Template Variables
            </CardTitle>
            <CardDescription>
              Configure test values for preview
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prompt.variables.map((variable) => (
                <div key={variable.name} className="flex items-start gap-4">
                  <div className="w-32 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {`{{${variable.name}}}`}
                      </Badge>
                      {variable.required && (
                        <span className="text-solution text-xs">*</span>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-1">{variable.type}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-secondary">{variable.description}</p>
                    {variable.type === 'string' && (
                      <input
                        type="text"
                        value={String(testVariables[variable.name] || '')}
                        onChange={(e) =>
                          setTestVariables((prev) => ({
                            ...prev,
                            [variable.name]: e.target.value,
                          }))
                        }
                        placeholder={`Enter ${variable.name}...`}
                        className="mt-2 w-full px-3 py-1.5 text-sm border border-border-subtle rounded-md bg-warm-50 focus:outline-none focus:ring-2 focus:ring-market/30"
                      />
                    )}
                    {variable.type === 'boolean' && (
                      <button
                        onClick={() =>
                          setTestVariables((prev) => ({
                            ...prev,
                            [variable.name]: !prev[variable.name],
                          }))
                        }
                        className={`mt-2 px-3 py-1 text-sm rounded-md border ${
                          testVariables[variable.name]
                            ? 'bg-market/10 border-market/30 text-market'
                            : 'bg-warm-50 border-border-subtle text-text-muted'
                        }`}
                      >
                        {testVariables[variable.name] ? 'true' : 'false'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Editor / Preview */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'preview' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('preview')}
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button
                variant={activeTab === 'edit' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('edit')}
              >
                <Code className="w-4 h-4" />
                Template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'preview' ? (
            <div className="space-y-4">
              {/* System Prompt */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-business/10 text-business border border-business/20">
                    System
                  </Badge>
                </div>
                <div className="p-4 bg-business/5 rounded-lg border border-business/10">
                  <pre className="text-sm text-text whitespace-pre-wrap font-sans">
                    {prompt.systemPrompt}
                  </pre>
                </div>
              </div>

              {/* User Prompt (Rendered) */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-market/10 text-market border border-market/20">
                    User
                  </Badge>
                  <span className="text-xs text-text-muted">(with test variables applied)</span>
                </div>
                <div className="p-4 bg-market/5 rounded-lg border border-market/10">
                  <pre className="text-sm text-text whitespace-pre-wrap font-sans">
                    {previewContent || '(empty - fill in required variables above)'}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* System Prompt Template */}
              <div>
                <label className="text-sm font-medium text-text-secondary mb-2 block">
                  System Prompt
                </label>
                <textarea
                  value={prompt.systemPrompt}
                  readOnly
                  className="w-full h-40 px-4 py-3 text-sm font-mono border border-border-subtle rounded-lg bg-warm-50 focus:outline-none resize-none"
                />
              </div>

              {/* User Template */}
              <div>
                <label className="text-sm font-medium text-text-secondary mb-2 block">
                  User Template
                </label>
                <textarea
                  value={prompt.userTemplate}
                  readOnly
                  className="w-full h-64 px-4 py-3 text-sm font-mono border border-border-subtle rounded-lg bg-warm-50 focus:outline-none resize-none"
                />
                <p className="text-xs text-text-muted mt-2">
                  Supports: <code className="bg-warm-100 px-1 rounded">{'{{variable}}'}</code>,{' '}
                  <code className="bg-warm-100 px-1 rounded">{'{{#if var}}...{{/if}}'}</code>,{' '}
                  <code className="bg-warm-100 px-1 rounded">{'{{#each arr}}...{{/each}}'}</code>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Output Format (if defined) */}
      {prompt.outputFormat && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Expected Output Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="p-4 bg-warm-50 rounded-lg text-sm font-mono overflow-x-auto">
              {JSON.stringify(prompt.outputFormat, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default PromptEditor
